import { ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import GlobalStyles from "@mui/material/GlobalStyles";
import { TABLE_SX } from "../styles";
import { INVOICE_SX } from "./InvoiceA4";

export type PrintOrientation = "portrait" | "landscape";

const ROOT_CLASS = "bdc-print-root";

/**
 * Prints `children` alone. The content is rendered into a node appended to <body>, and while
 * it is mounted a print stylesheet hides every other child of <body> (the app with its header
 * and sidebar). Mounting it calls window.print(); `onDone` fires after the dialog closes.
 *
 * Mounted only for the moment of printing, so the on-screen tables are never rendered twice.
 */
export default function PrintPortal({
  children,
  orientation,
  onDone,
}: Readonly<{ children: ReactNode; orientation: PrintOrientation; onDone: () => void }>) {
  const [node] = useState(() => {
    const div = document.createElement("div");
    div.className = ROOT_CLASS;
    return div;
  });

  useEffect(() => {
    document.body.appendChild(node);
    const finish = () => onDone();
    window.addEventListener("afterprint", finish);
    // let the portal paint before the dialog snapshots the page
    const timer = window.setTimeout(() => window.print(), 100);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("afterprint", finish);
      node.remove();
    };
  }, [node, onDone]);

  return (
    <>
      <GlobalStyles
        styles={{
          [`.${ROOT_CLASS}`]: { display: "none" },
          "@page": { size: `A4 ${orientation}`, margin: "10mm" },
          "@media print": {
            [`body > *:not(.${ROOT_CLASS})`]: { display: "none !important" },
            [`.${ROOT_CLASS}`]: { display: "block !important" },
            body: { background: "#fff !important" },
          },
        }}
      />
      {createPortal(
        <Box
          sx={{
            ...TABLE_SX,
            ...INVOICE_SX,
            fontFamily: "'Prompt', sans-serif",
            color: "#000",
            "& .bdc-t th": { position: "static" },
            "& .bdc-t": { fontSize: "9.5px" },
            "& .bdc-t td, & .bdc-t th": { border: "1px solid #BFC3CB", padding: "3px 4px" },
            "& .bdc-t th.title": {
              writingMode: "horizontal-tb",
              transform: "none",
              textAlign: "left",
              fontSize: "11px",
              fontWeight: 600,
              color: "#000",
              background: "#fff",
              border: 0,
              padding: "0 0 6px",
            },
            "& .bdc-t tr": { breakInside: "avoid" },
            "& .bdc-print-block": { breakAfter: "page" },
            "& .bdc-pv th.sk": { height: 96, fontSize: "9px" },
            "& .bdc-pv tr.gt td, & .bdc-pv tr.ls td, & .bdc-pv tr.lh td": {
              printColorAdjust: "exact",
              WebkitPrintColorAdjust: "exact",
            },
            "& .bdc-a4": { border: 0, boxShadow: "none", width: "auto", minHeight: 0, padding: 0 },
          }}
        >
          {children}
        </Box>,
        node,
      )}
    </>
  );
}
